import React, { useState } from "react";
import "./PollCard.css";

interface PollOptionDto {
  optionId: string;
  optionText: string;
  emoji?: string;
  voteCount: number;
}

interface PollDto {
  pollId: string;
  title: string;
  category: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  imageUrl?: string;
  createdByUserId?: string;
  createdByDisplayName?: string;
  createdAt: string;
  options: PollOptionDto[];
}

interface PollCardProps {
  poll: PollDto;
  onVote?: (pollId: string, optionId: string) => void;
  onEdit?: (poll: PollDto) => void;
  onDelete?: (pollId: string) => void;
  editable?: boolean;
}

export default function PollCard({
  poll,
  onVote,
  onEdit,
  onDelete,
  editable = false,
}: PollCardProps) {
  const [interacted, setInteracted] = useState(false);
  const totalVotes = poll.options.reduce((sum, o) => sum + o.voteCount, 0);

  const handleVoteClick = (optionId: string) => {
    if (onVote) {
      onVote(poll.pollId, optionId);
      setInteracted(true);
    }
  };

  return (
    <div
      className="poll-card"
      style={{
        // set CSS variables for theming
        "--primary-color": poll.primaryColor ?? "#4f46e5",
        "--secondary-color": poll.secondaryColor ?? "#fff",
        fontFamily: poll.fontFamily ?? "Inter, sans-serif",
      } as React.CSSProperties}
    >
      <h3 className="poll-title">{poll.title}</h3>
      <p className="category">Category: {poll.category}</p>
      {poll.imageUrl && (
        <img src={poll.imageUrl} alt={poll.title} className="poll-image" />
      )}

      {!interacted ? (
        <div className="options">
          {poll.options.map((opt) => (
            <button
              key={opt.optionId}
              onClick={() => handleVoteClick(opt.optionId)}
              className="vote-btn"
            >
              {opt.emoji ? `${opt.emoji} ` : ""}
              {opt.optionText}
            </button>
          ))}
        </div>
      ) : (
        <div className="results-graph">
          {poll.options.map((opt) => {
            const percent = totalVotes > 0 ? (opt.voteCount / totalVotes) * 100 : 0;
            return (
              <div key={opt.optionId} className="result-row">
                <span className="option-label">
                  {opt.emoji ? `${opt.emoji} ` : ""}
                  {opt.optionText}
                </span>
                <div className="bar">
                  <div
                    className="bar-fill"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <span className="vote-count">
                  {opt.voteCount} ({percent.toFixed(1)}%)
                </span>
              </div>
            );
          })}
          <div className="total-votes">Total Votes: {totalVotes}</div>
        </div>
      )}

      <div className="poll-footer">
        <div className="created-by">
          Created by: {poll.createdByDisplayName || "Anonymous"}{" "}
          {new Date(poll.createdAt).toLocaleDateString()}
        </div>
        {editable && (
          <div className="actions">
            {onEdit && <button onClick={() => onEdit(poll)}>Edit</button>}
            {onDelete && <button onClick={() => onDelete(poll.pollId)}>Delete</button>}
          </div>
        )}
      </div>
    </div>
  );
}
